import { Alert, Button, Card, EmptyState, PageHeader, Skeleton, useToast } from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { CalendarDays, CalendarOff, Copy, Plus } from 'lucide-react';
import { type JSX, useState } from 'react';

import { useCurrentUser } from '../../auth/use-current-user';
import {
  createMenuCalendarEntry,
  deleteMenuCalendarEntry,
  getDietarySummary,
  listCalendarEntries,
  publishMenuCalendarEntry,
  updateMenuCalendarEntry,
  type DietaryCoverageEntry,
  type MenuCalendarEntry,
} from '../../calendar/api';
import { mapMenuCalendarEntryError } from '../../calendar/calendar-api-errors';
import {
  DEFAULT_MENU_CALENDAR_ENTRY_FORM_VALUES,
  toMenuCalendarEntryCreateInput,
  toMenuCalendarEntryFormValues,
  toMenuCalendarEntryUpdateInput,
  type MenuCalendarEntryFormValues,
} from '../../calendar/menu-calendar-entry-form-schema';
import { GuideLink } from '../../guide/guide-link';
import { hasPermission } from '../../auth/has-permission';
import { listMenuPositions, listServiceTypes, searchAllMenus } from '../../menus/api';
import { CalendarFilters } from './components/calendar-filters';
import { CalendarGrid, type CalendarGridMenu, type CalendarGridPosition } from './components/calendar-grid';
import { CopyWeekModal } from './components/copy-week-modal';
import { DietaryWarningModal } from './components/dietary-warning-modal';
import { MenuCalendarEntryModal } from './components/menu-calendar-entry-modal';

// `protectedRoute` in `router.tsx` is pathless (`id: 'protected'`), so a
// child declared with `path: '/calendar'` still gets nested under it in the
// route id, even though its URL is the absolute `/calendar`.
const calendarRoute = getRouteApi('/protected/calendar');

type EntryModalState =
  | { mode: 'create'; prefill?: { serviceDate: string; positionId: string } }
  | { mode: 'edit'; entry: MenuCalendarEntry }
  | null;
interface PendingPublish {
  entry: MenuCalendarEntry;
  warnings: string[];
}

/**
 * `menu-calendar/spec.md` → "Pantalla de calendario en back-office". List
 * state lives in the URL (`calendarListSearchSchema` in `router.tsx`), same
 * convention as `ContractListPage`. `serviceTypeId` filters client-side
 * (`CalendarFilters`'s doc comment); `from`/`to`/`isPublished` reach
 * `GET /calendar` directly.
 */
export function CalendarListPage(): JSX.Element {
  const search = calendarRoute.useSearch();
  const navigate = calendarRoute.useNavigate();
  const appNavigate = useNavigate();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [entryModal, setEntryModal] = useState<EntryModalState>(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [pendingPublish, setPendingPublish] = useState<PendingPublish | null>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const entriesQuery = useQuery({
    queryKey: ['calendar', search.from, search.to, search.positionId, search.isPublished],
    queryFn: () =>
      listCalendarEntries({
        from: search.from,
        to: search.to,
        positionId: search.positionId,
        isPublished: search.isPublished,
      }),
  });

  const serviceTypesQuery = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });
  const positionsQuery = useQuery({ queryKey: ['menu-positions'], queryFn: listMenuPositions });

  // Menu names are resolved client-side over the full catalog (same
  // "GET without server-side filtering that matters here" criterion
  // `ContractListPage` applies to `searchCustomers`): an entry can reference
  // an archived menu (`menu-calendar/spec.md` → "Entrada con menú archivado
  // sigue visible en el histórico"), so `status` is left unset to include
  // every menu regardless of `isActive`/`deletedAt`.
  const menusQuery = useQuery({
    queryKey: ['menus-for-calendar'],
    queryFn: () =>
      searchAllMenus({
        q: undefined,
        serviceTypeId: undefined,
        dietaryTagId: undefined,
        excludeAllergenId: undefined,
        status: undefined,
      }),
  });

  /**
   * `menu-calendar/spec.md` → "Advertencia dietaria no bloqueante en la
   * grilla": drives both the per-row warning icon (`CalendarGrid`) and the
   * publish-time confirmation check below, from the same source, so
   * neither the icon nor the confirmation ever disagrees with the other.
   */
  const dietarySummaryQuery = useQuery({
    queryKey: ['calendar-dietary-summary', search.from, search.to],
    queryFn: () => getDietarySummary(search.from, search.to),
  });

  const dietaryCoverageByDate: Record<string, DietaryCoverageEntry[]> = Object.fromEntries(
    (dietarySummaryQuery.data ?? []).map((entry) => [entry.date, entry.coverage]),
  );

  async function invalidateEntries(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['calendar'] }),
      queryClient.invalidateQueries({ queryKey: ['calendar-dietary-summary'] }),
    ]);
  }

  const createMutation = useMutation({
    mutationFn: createMenuCalendarEntry,
    onSuccess: async () => {
      await invalidateEntries();
      setEntryModal(null);
      toast('success', 'Entrada creada');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: ReturnType<typeof toMenuCalendarEntryUpdateInput>;
    }) => updateMenuCalendarEntry(id, input),
    onSuccess: async () => {
      await invalidateEntries();
      setEntryModal(null);
      toast('success', 'Entrada actualizada');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMenuCalendarEntry,
    onMutate: () => setActionError(undefined),
    onSuccess: async () => {
      await invalidateEntries();
      toast('success', 'Entrada eliminada');
    },
    onError: (error) => setActionError(mapMenuCalendarEntryError(error)),
  });

  const publishMutation = useMutation({
    mutationFn: ({ id, isPublished }: { id: string; isPublished: boolean }) =>
      publishMenuCalendarEntry(id, isPublished),
    onMutate: () => setActionError(undefined),
    onSuccess: async (result) => {
      await invalidateEntries();
      toast('success', result.entry.isPublished ? 'Entrada publicada' : 'Entrada despublicada');
    },
    onError: (error) => setActionError(mapMenuCalendarEntryError(error)),
  });

  const canManage = hasPermission(currentUser.data, 'menu.calendar.manage');

  function applyFilters(patch: Partial<typeof search>): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  const menusById: Record<string, CalendarGridMenu> = Object.fromEntries(
    (menusQuery.data?.items ?? []).map((menu) => [menu.id, { id: menu.id, name: menu.name }]),
  );

  const modalMenus = (menusQuery.data?.items ?? []).map((menu) => ({
    id: menu.id,
    name: menu.name,
    serviceTypeId: menu.serviceTypeId,
  }));

  const activePositions: CalendarGridPosition[] = (positionsQuery.data ?? []).filter(
    (position) => position.isActive,
  );

  const visiblePositions = activePositions.filter(
    (position) => search.serviceTypeId === undefined || position.serviceTypeId === search.serviceTypeId,
  );

  // The edit modal must still resolve a label for the entry's own position
  // even when it was deactivated after the entry was created
  // (`menu-calendar/spec.md` → "Entrada con menú archivado sigue visible en
  // el histórico", same criterion applied to positions).
  const modalPositions =
    entryModal?.mode === 'edit' &&
    !activePositions.some((position) => position.id === entryModal.entry.positionId)
      ? [...activePositions, entryModal.entry.position]
      : activePositions;

  const entries = (entriesQuery.data ?? []).filter((entry) => {
    if (search.serviceTypeId === undefined) {
      return true;
    }

    return entry.position.serviceTypeId === search.serviceTypeId;
  });

  const dates = enumerateIsoDates(search.from, search.to);

  /**
   * `menu-calendar/spec.md` → "Publicación explícita": the dietary check
   * runs client-side, against the same `dietarySummaryQuery` data the
   * warning icon already uses, *before* any `PATCH /calendar/:id/publish`
   * call — not after, as `tasks.md`'s GREEN task text literally reads
   * ("ya se hizo al abrir el modal"). That literal reading would leave
   * "Cancelar" unable to revert a publish that already persisted without a
   * second API call. Evaluating locally first is the only reading
   * consistent with the spec's own "modal... antes de confirmar la
   * publicación" and keeps "Cancelar" a true no-op (nothing was ever
   * persisted). See `apply-progress.md` for the full reconciliation.
   */
  function handleTogglePublish(entry: MenuCalendarEntry): void {
    if (entry.isPublished) {
      publishMutation.mutate({ id: entry.id, isPublished: false });
      return;
    }

    const coverage = dietaryCoverageByDate[entry.serviceDate] ?? [];
    const missing = coverage.filter((item) => !item.covered).map((item) => `MISSING_${item.code}`);

    if (missing.length > 0) {
      setPendingPublish({ entry, warnings: missing });
      return;
    }

    publishMutation.mutate({ id: entry.id, isPublished: true });
  }

  function handleConfirmPublish(): void {
    if (pendingPublish === null) {
      return;
    }

    publishMutation.mutate({ id: pendingPublish.entry.id, isPublished: true });
    setPendingPublish(null);
  }

  function handleCancelPublish(): void {
    setPendingPublish(null);
  }

  const entryFormError =
    entryModal?.mode === 'create'
      ? mapMenuCalendarEntryError(createMutation.error)
      : entryModal?.mode === 'edit'
        ? mapMenuCalendarEntryError(updateMutation.error)
        : undefined;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={CalendarDays}
        title="Calendario"
        description="Planificá qué menús se ofrecen cada fecha y publicalos para el cliente."
        actions={
          <div className="flex items-center gap-2">
            {canManage ? (
              <>
                <Button
                  variant="secondary"
                  icon={CalendarOff}
                  onClick={() => void appNavigate({ to: '/calendar/holidays' })}
                >
                  Feriados
                </Button>
                <Button variant="secondary" icon={Copy} onClick={() => setCopyModalOpen(true)}>
                  Copiar semana
                </Button>
                <Button icon={Plus} onClick={() => setEntryModal({ mode: 'create' })}>
                  Nueva entrada
                </Button>
              </>
            ) : null}
            <GuideLink moduleId="calendar" />
          </div>
        }
      />

      <Card>
        <CalendarFilters
          from={search.from}
          to={search.to}
          serviceTypeId={search.serviceTypeId}
          positionId={search.positionId}
          isPublished={search.isPublished}
          serviceTypes={serviceTypesQuery.data ?? []}
          positions={activePositions}
          onFromChange={(from) => applyFilters({ from })}
          onToChange={(to) => applyFilters({ to })}
          onServiceTypeChange={(serviceTypeId) => applyFilters({ serviceTypeId })}
          onPositionChange={(positionId) => applyFilters({ positionId })}
          onPublishedChange={(isPublished) => applyFilters({ isPublished })}
        />
      </Card>

      {actionError !== undefined ? <Alert tone="danger">{actionError}</Alert> : null}

      {entriesQuery.isError ? (
        <Alert tone="danger" title="No pudimos cargar el calendario">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void entriesQuery.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {entriesQuery.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton data-testid="calendar-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="calendar-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="calendar-skeleton-row" className="h-10 w-full" />
        </div>
      ) : null}

      {entriesQuery.data !== undefined && entries.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="Todavía no hay entradas planificadas."
          description="Elegí otro rango de fechas o planificá la primera entrada."
        />
      ) : null}

      {entriesQuery.data !== undefined && entries.length > 0 ? (
        <CalendarGrid
          dates={dates}
          serviceTypes={serviceTypesQuery.data ?? []}
          positions={visiblePositions}
          entries={entries}
          menusById={menusById}
          canManage={canManage}
          dietaryCoverageByDate={dietaryCoverageByDate}
          onEdit={(entry) => setEntryModal({ mode: 'edit', entry })}
          onTogglePublish={handleTogglePublish}
          onDelete={(entry) => {
            if (window.confirm('¿Eliminar esta entrada del calendario?')) {
              deleteMutation.mutate(entry.id);
            }
          }}
          onAssign={(date, position) =>
            setEntryModal({
              mode: 'create',
              prefill: { serviceDate: date, positionId: position.id },
            })
          }
        />
      ) : null}

      {entryModal !== null ? (
        <MenuCalendarEntryModal
          mode={entryModal.mode}
          defaultValues={
            entryModal.mode === 'edit'
              ? toMenuCalendarEntryFormValues(entryModal.entry)
              : entryModal.prefill === undefined
                ? undefined
                : {
                    ...DEFAULT_MENU_CALENDAR_ENTRY_FORM_VALUES,
                    serviceDate: entryModal.prefill.serviceDate,
                    positionId: entryModal.prefill.positionId,
                  }
          }
          menus={modalMenus}
          positions={modalPositions}
          submitting={createMutation.isPending || updateMutation.isPending}
          formError={entryFormError === '' ? undefined : entryFormError}
          onCancel={() => setEntryModal(null)}
          onSubmit={async (values: MenuCalendarEntryFormValues) => {
            if (entryModal.mode === 'create') {
              await createMutation
                .mutateAsync(toMenuCalendarEntryCreateInput(values))
                .catch(() => undefined);
            } else {
              await updateMutation
                .mutateAsync({
                  id: entryModal.entry.id,
                  input: toMenuCalendarEntryUpdateInput(values),
                })
                .catch(() => undefined);
            }
          }}
        />
      ) : null}

      {pendingPublish !== null ? (
        <DietaryWarningModal
          warnings={pendingPublish.warnings}
          onConfirm={handleConfirmPublish}
          onCancel={handleCancelPublish}
        />
      ) : null}

      {copyModalOpen ? (
        <CopyWeekModal
          positions={(positionsQuery.data ?? []).map((position) => ({
            id: position.id,
            name: position.name,
          }))}
          onClose={() => setCopyModalOpen(false)}
          onApplied={() => void invalidateEntries()}
        />
      ) : null}
    </section>
  );
}

function enumerateIsoDates(from: string, to: string): string[] {
  const dates: string[] = [];
  const cursor = new Date(`${from}T00:00:00.000Z`);
  const end = new Date(`${to}T00:00:00.000Z`);

  while (cursor.getTime() <= end.getTime()) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return dates;
}
